import React, { useMemo, useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import { Row, Col, Badge, Button, Modal, Form } from "react-bootstrap";
import ClickBack from "../../components/buttons/ClickBack";
import DynamicTable from "../../components/DynamicTable";
import {
  fetchAccountDeletionRequests,
  fetchAccountDeletionRequestStats,
  updateAccountDeletionRequest,
  deleteAccountDeletionRequest
} from "./dataService";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import SearchBar from "../../components/SearchBar";
import { toast } from "react-toastify";

function ListScreen(props) {
  const [requestsData, setRequestsData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [nbr, setNbr] = useState(1);

  // Modal pour modifier le statut
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [adminNotes, setAdminNotes] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      const [requestsResponse, statsResponse] = await Promise.all([
        fetchAccountDeletionRequests(nbr, searchTerm, statusFilter),
        fetchAccountDeletionRequestStats()
      ]);
      setRequestsData(requestsResponse.data);
      setStats(statsResponse.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setNbr(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setNbr(1);
  };

  useEffect(() => {
    fetchData();
  }, [nbr, searchTerm, statusFilter]);

  const handleOpenModal = (request) => {
    setSelectedRequest(request);
    setNewStatus(request.status);
    setAdminNotes(request.admin_notes || "");
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
    setNewStatus("");
    setAdminNotes("");
  };

  const handleUpdateStatus = async () => {
    if (!selectedRequest) return;

    try {
      await updateAccountDeletionRequest(selectedRequest.id, {
        status: newStatus,
        admin_notes: adminNotes,
      });
      toast.success("Statut mis à jour avec succès");
      handleCloseModal();
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette demande ?")) {
      return;
    }

    try {
      await deleteAccountDeletionRequest(id);
      toast.success("Demande supprimée avec succès");
      fetchData();
    } catch (error) {
      console.error("Error deleting request:", error);
      toast.error(error.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const columns = useMemo(() => {
    return [
      { Header: "ID", accessor: "id" },
      {
        Header: "Utilisateur",
        accessor: "email",
        Cell: ({ row }) => {
          const request = row.original;
          return (
            <div>
              <div><strong>{request.email}</strong></div>
              <div className="text-muted small">
                {request.prenom} {request.nom}
              </div>
            </div>
          );
        }
      },
      {
        Header: "Date de demande",
        accessor: "requested_at",
        Cell: ({ value }) =>
          format(new Date(value), "dd/MM/yyyy HH:mm", { locale: fr }),
      },
      {
        Header: "Statut",
        accessor: "status",
        Cell: ({ value }) => {
          const statusColors = {
            'pending': 'bg-warning text-dark',
            'processing': 'bg-info',
            'completed': 'bg-success',
            'rejected': 'bg-danger'
          };
          const statusLabels = {
            'pending': 'En attente',
            'processing': 'En cours',
            'completed': 'Complétée',
            'rejected': 'Rejetée'
          };
          return (
            <Badge className={statusColors[value] || 'bg-secondary'}>
              {statusLabels[value] || value}
            </Badge>
          );
        }
      },
      {
        Header: "Traité par",
        accessor: "processed_by_admin",
        Cell: ({ row }) => {
          const admin = row.original.processed_by_admin;
          if (!admin) return <span className="text-muted">-</span>;
          return `${admin.prenom || ''} ${admin.nom || ''}`;
        }
      },
      {
        Header: "Date de traitement",
        accessor: "processed_at",
        Cell: ({ value }) => {
          if (!value) return <span className="text-muted">-</span>;
          return format(new Date(value), "dd/MM/yyyy HH:mm", { locale: fr });
        }
      },
      {
        Header: "IP",
        accessor: "ip_address",
        Cell: ({ value }) => value || <span className="text-muted">-</span>
      },
      {
        Header: "Actions",
        Cell: ({ row }) => {
          const request = row.original;
          return (
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="primary"
                onClick={() => handleOpenModal(request)}
              >
                Modifier
              </Button>
              {(request.status === 'completed' || request.status === 'rejected') && (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => handleDelete(request.id)}
                >
                  Supprimer
                </Button>
              )}
            </div>
          );
        }
      }
    ].filter(Boolean);
  }, []);

  if (!requestsData) {
    return (
      <Container>
        <p>Chargement...</p>
      </Container>
    );
  }

  return (
    <Container>
      <div className="accountDeletionRequestsList">
        <p className="h6">Accueil / RGPD & Sécurité / Demandes de suppression</p>

        <Row className="pt-4 pb-2">
          <Col style={{ display: "flex", alignItems: "center" }}>
            <ClickBack />
            <h1 className="p-1">Demandes de suppression de compte</h1>
          </Col>
        </Row>

        {stats && (
          <Row className="mb-3">
            <Col md={3}>
              <div className="card p-3">
                <h6>Total</h6>
                <h3>{stats.total}</h3>
              </div>
            </Col>
            <Col md={3}>
              <div className="card p-3 border-warning">
                <h6>En attente</h6>
                <h3 className="text-warning">{stats.pending}</h3>
              </div>
            </Col>
            <Col md={3}>
              <div className="card p-3 border-info">
                <h6>En cours</h6>
                <h3 className="text-info">{stats.processing}</h3>
              </div>
            </Col>
            <Col md={3}>
              <div className="card p-3 border-success">
                <h6>Complétées</h6>
                <h3 className="text-success">{stats.completed}</h3>
              </div>
            </Col>
          </Row>
        )}

        <Row className="mb-3">
          <Col md={6}>
            <SearchBar searchTerm={searchTerm} handleSearch={handleSearch} />
          </Col>
          <Col md={6}>
            <Form.Select value={statusFilter} onChange={handleStatusFilterChange}>
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="processing">En cours</option>
              <option value="completed">Complétée</option>
              <option value="rejected">Rejetée</option>
            </Form.Select>
          </Col>
        </Row>

        {loading ? (
          <p className="lead">Chargement...</p>
        ) : (
          <DynamicTable
            columns={columns}
            data={requestsData?.data}
            link={requestsData?.links}
            last_page={requestsData?.last_page}
            setNbr={setNbr}
            nbr={nbr}
            total={requestsData?.total}
            name="demande"
          />
        )}
      </div>

      {/* Modal pour modifier le statut */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier la demande de suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <>
              <div className="mb-3">
                <strong>Utilisateur:</strong> {selectedRequest.email}<br/>
                <strong>Nom:</strong> {selectedRequest.prenom} {selectedRequest.nom}
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Statut</Form.Label>
                <Form.Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <option value="pending">En attente</option>
                  <option value="processing">En cours</option>
                  <option value="completed">Complétée</option>
                  <option value="rejected">Rejetée</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Notes administrateur</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Notes internes sur le traitement de cette demande..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleUpdateStatus}>
            Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ListScreen;
